// Analytics Utility for Tracking User Interactions
// Supports PostHog, Google Analytics 4 (GA4), and custom backend tracking

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Send event to backend
const sendToBackend = async (eventType, eventName, pagePath = null, metadata = {}) => {
  try {
    await fetch(`${BACKEND_URL}/api/analytics/track?session_id=${getSessionId()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        event_name: eventName,
        page_path: pagePath || window.location.pathname,
        metadata
      })
    });
  } catch (error) {
    console.error('[Analytics] Backend tracking error:', error);
  }
};

// Send event to PostHog
const sendToPostHog = (eventName, properties = {}) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(eventName, properties);
  }
};

class AnalyticsManager {
  constructor() {
    this.isGAEnabled = typeof window !== 'undefined' && window.gtag;
    this.isPostHogEnabled = typeof window !== 'undefined' && window.posthog;
    this.debug = process.env.NODE_ENV === 'development';
  }

  // Track page views
  trackPageView(pagePath, pageTitle) {
    if (this.debug) {
      console.log('[Analytics] Page View:', { pagePath, pageTitle });
    }
    
    // Send to backend
    sendToBackend('page_view', `view_${pagePath.replace(/\//g, '_') || 'home'}`, pagePath, { title: pageTitle });
    
    // Send to PostHog
    sendToPostHog('$pageview', { path: pagePath, title: pageTitle });
    
    // Send to GA4
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    }
  }

  // Track button clicks
  trackButtonClick(buttonName, location) {
    if (this.debug) {
      console.log('[Analytics] Button Click:', { buttonName, location });
    }
    
    sendToBackend('button_click', buttonName, null, { location });
    sendToPostHog('button_click', { button: buttonName, location });
    
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', 'button_click', {
        event_category: 'Engagement',
        event_label: buttonName,
        location: location,
      });
    }
  }

  // Track CTA clicks (Book Demo, Contact Sales)
  trackCTAClick(ctaType, source) {
    if (this.debug) {
      console.log('[Analytics] CTA Click:', { ctaType, source });
    }
    
    sendToBackend('cta_click', ctaType, null, { source });
    sendToPostHog('cta_click', { cta_type: ctaType, source });
    
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'Conversion',
        event_label: ctaType,
        source: source,
      });
    }
  }

  // Track form submissions
  trackFormSubmit(formType, success = true, metadata = {}) {
    if (this.debug) {
      console.log('[Analytics] Form Submit:', { formType, success });
    }
    
    const eventName = success ? `${formType}_submit` : `${formType}_error`;
    sendToBackend('form_submit', eventName, null, { success, ...metadata });
    sendToPostHog(eventName, { form_type: formType, success, ...metadata });
    
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', success ? 'form_submit_success' : 'form_submit_error', {
        event_category: 'Conversion',
        event_label: formType,
      });
    }
  }

  // Track agent interactions
  trackAgentInteraction(action, agentId, agentName) {
    if (this.debug) {
      console.log('[Analytics] Agent Interaction:', { action, agentId, agentName });
    }
    
    sendToBackend('agent_interaction', `agent_${action}`, null, { agentId, agentName });
    sendToPostHog(`agent_${action}`, { agent_id: agentId, agent_name: agentName });
    
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', `agent_${action}`, {
        event_category: 'Agent Discovery',
        event_label: agentName,
        agent_id: agentId,
      });
    }
  }

  // Track workflow interactions
  trackWorkflowInteraction(action, workflowId, workflowName) {
    if (this.debug) {
      console.log('[Analytics] Workflow Interaction:', { action, workflowId, workflowName });
    }
    
    sendToBackend('workflow_interaction', `workflow_${action}`, null, { workflowId, workflowName });
    sendToPostHog(`workflow_${action}`, { workflow_id: workflowId, workflow_name: workflowName });
  }

  // Track simulator interactions
  trackSimulatorRun(workflowName) {
    if (this.debug) {
      console.log('[Analytics] Simulator Run:', { workflowName });
    }
    
    sendToBackend('simulator', 'simulator_run', '/workflow-simulator', { workflow: workflowName });
    sendToPostHog('simulator_run', { workflow: workflowName });
  }

  // Track marketplace filters
  trackFilterUse(filterType, filterValue) {
    if (this.debug) {
      console.log('[Analytics] Filter Used:', { filterType, filterValue });
    }
    
    sendToBackend('filter', `filter_${filterType}`, null, { filterType, filterValue });
    sendToPostHog('filter_used', { filter_type: filterType, filter_value: filterValue });
    
    if (this.isGAEnabled && window.gtag) {
      window.gtag('event', 'filter_used', {
        event_category: 'Agent Discovery',
        event_label: `${filterType}: ${filterValue}`,
      });
    }
  }

  // Track navigation
  trackNavigation(from, to, via) {
    if (this.debug) {
      console.log('[Analytics] Navigation:', { from, to, via });
    }
    
    sendToBackend('navigation', `nav_${via}`, to, { from, via });
    sendToPostHog('navigation', { from, to, via });
  }

  // Track scroll depth
  trackScrollDepth(percentage) {
    if (percentage % 25 === 0) {
      if (this.debug) {
        console.log('[Analytics] Scroll Depth:', percentage + '%');
      }
      
      sendToBackend('scroll', `scroll_${percentage}`, null, { percentage });
      sendToPostHog('scroll_depth', { percentage });
    }
  }

  // Track errors
  trackError(errorType, errorMessage, location) {
    if (this.debug) {
      console.error('[Analytics] Error Tracked:', { errorType, errorMessage, location });
    }
    
    sendToBackend('error', errorType, location, { message: errorMessage });
    sendToPostHog('error', { type: errorType, message: errorMessage, location });
  }
}

// Create singleton instance
export const analytics = new AnalyticsManager();

// Convenience exports
export const trackPageView = (path, title) => analytics.trackPageView(path, title);
export const trackButtonClick = (name, loc) => analytics.trackButtonClick(name, loc);
export const trackCTAClick = (type, src) => analytics.trackCTAClick(type, src);
export const trackFormSubmit = (type, success, meta) => analytics.trackFormSubmit(type, success, meta);
export const trackAgentInteraction = (action, id, name) => analytics.trackAgentInteraction(action, id, name);
export const trackWorkflowInteraction = (action, id, name) => analytics.trackWorkflowInteraction(action, id, name);
export const trackSimulatorRun = (name) => analytics.trackSimulatorRun(name);
export const trackFilterUse = (type, value) => analytics.trackFilterUse(type, value);
export const trackNavigation = (from, to, via) => analytics.trackNavigation(from, to, via);
export const trackScrollDepth = (pct) => analytics.trackScrollDepth(pct);
export const trackError = (type, msg, loc) => analytics.trackError(type, msg, loc);
